/**
 * Recherche dans la liste des gammes : un seul champ en haut, qui évite de passer la liste en revue
 * ligne par ligne.
 *
 * On cherche dans le code, le libellé, la marque, les modèles de machine (texte saisi ET clé) et les
 * références constructeur. La saisie est comparée sans tenir compte des majuscules, des accents, des
 * espaces ni de la ponctuation (« kaeser sk-24 » trouve « KAESER SK24 »), et le « + » compte comme
 * dans la clé d'un modèle : « dd35+ » ne trouve pas un modèle « DD35 » sans « + ».
 *
 * Plusieurs mots se cumulent : chacun doit se retrouver quelque part dans la gamme. Un mot se
 * cherche dans un champ entier (« DD1050F ») ou dans un mot du libellé (« 35+ » dans
 * « DD/DDp 32 = 35+ »), jamais à cheval sur deux champs.
 *
 * Fonctions pures, sans React.
 */
import type { GammeResume } from '../api/client';

/**
 * Forme comparable d'un texte : majuscules, accents retirés, « + » devenu `PLUS`, tout ce qui n'est
 * ni lettre ni chiffre supprimé. C'est la règle de la clé de modèle de l'API, volontairement.
 */
export function cleRecherche(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/\+/g, ' PLUS ')
    .replace(/[^A-Z0-9]/g, '');
}

/** Les mots d'une saisie, chacun sous forme comparable ; ceux qui ne laissent rien sont écartés. */
export function jetonsDeRecherche(saisie: string): string[] {
  return saisie
    .split(/\s+/)
    .map(cleRecherche)
    .filter((j) => j !== '');
}

/** Tout ce qu'on peut retrouver d'une gamme : chaque champ entier, plus chaque mot du libellé. */
function entreesDeRecherche(g: GammeResume): string[] {
  const champs = [
    g.code,
    g.label,
    g.brandName,
    ...g.criteres.flatMap((c) => [
      c.modelKey,
      c.modelLabel ?? '',
      c.manufacturerReferenceKey,
      c.manufacturerReferenceLabel ?? '',
    ]),
    ...g.referencesConstructeur,
  ];
  const motsDuLibelle = g.label.split(/[^\p{L}\p{N}+]+/u);
  return [...champs, ...motsDuLibelle].map(cleRecherche).filter((e) => e !== '');
}

/** Vrai si chaque mot de la saisie se retrouve dans la gamme. */
export function gammeCorrespond(g: GammeResume, jetons: readonly string[]): boolean {
  if (jetons.length === 0) return true;
  const entrees = entreesDeRecherche(g);
  return jetons.every((j) => entrees.some((e) => e.includes(j)));
}

/** Les gammes qui répondent à la saisie, dans le même ordre ; toutes si la saisie est vide. */
export function filtrerGammes(gammes: readonly GammeResume[], saisie: string): GammeResume[] {
  const jetons = jetonsDeRecherche(saisie);
  return gammes.filter((g) => gammeCorrespond(g, jetons));
}
